import { useTranslation } from 'react-i18next';
import { ActionsColumn as ActionsColumnCommon } from '@otp/ui';
import {
  Download,
  Configure,
  GroupBookmark,
  Bookmark,
  Play,
  Remove,
  Archive,
} from '@otp/icons';
import { ResourceListItem } from '../../../types';
import { Actions, AppArea, Subjects } from 'src/graphql/generated/graphql';
import { useCan } from 'src/utils/can';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import { ResourcePageType } from 'src/graphql/generated/graphql';
import { BookmarkAction } from './bookmark';
import { useAuthContext } from 'src/context/auth.context';
import { useBookmarks } from 'src/components/resource/list/table/columns/actions/use-bookmarks';
import { VideoModal } from './video-modal';
import { useFullResource } from './use-full-resource';
import { getIsOtpProAndHigher } from 'src/utils/user-data';
import { useResourceDelete } from 'src/hooks/query/resources/use-resource-delete';
import { useState } from 'react';
import { DeleteConfirmation } from 'src/components/common/form/delete-confirmation';
import { useResourceDisable } from 'src/hooks/query/resources/use-resource-disabled';
import { useResourceActivate } from 'src/hooks/query/resources/use-resource-activate';

type ActionsColumnProps = {
  resource: ResourceListItem;
};

export const ActionsColumn = ({ resource }: ActionsColumnProps) => {
  const { user } = useAuthContext();

  const navigate = useNavigate();
  const { can } = useCan();

  const [deleteMutation] = useResourceDelete();
  const [disableMutation] = useResourceDisable();
  const [activateMutation] = useResourceActivate();

  const [isDeleting, setIsDeleting] = useState(false);

  const onDelete = () => {
    deleteMutation({
      variables: {
        id: resource.id,
      },
      onCompleted: () => {
        setIsDeleting(false);
      },
    });
  };

  const onDisable = () => {
    disableMutation({
      variables: {
        id: resource.id,
      },
      onCompleted: () => {
        setIsDeleting(false);
      },
    });
  };

  const onActivate = () => {
    activateMutation({
      variables: {
        id: resource.id,
      },
      onCompleted: () => {
        setIsDeleting(false);
      },
    });
  };

  const type = resource.type;

  const hasWriteAccess = can({
    action: Actions.Write,
    subject: Subjects.Resources,
    appArea: AppArea.Admin,
  });

  const hasBookmarksWritePermission = can({
    action: Actions.Write,
    subject: Subjects.ResourceFileBookmark,
    appArea: AppArea.Resource,
  });

  const { t } = useTranslation();

  const { onDownload, onPlay, onClosePlay, fullResource, isPlaying } =
    useFullResource({ resource });

  const { onBookmarkAdd, onBookmarkRemove, teamBookmark, personalBookmark } =
    useBookmarks({ resource });

  const onEdit = () => {
    navigate(`${Routes.adminResources.path}/${resource.id}/edit?type=${type}`);
  };

  const actions = [
    ...(hasWriteAccess
      ? [
          {
            id: 'edit',
            label: t('resources.list.columns.actions.edit'),
            icon: <Configure />,
            onClick: onEdit,
          },
          {
            id: 'delete',
            label: t('resources.list.columns.actions.delete'),
            icon: <Remove />,
            onClick: () => setIsDeleting(true),
          },
          {
            id: 'activate',
            label: resource.isDisabled
              ? t('resources.list.columns.actions.activate')
              : t('resources.list.columns.actions.disable'),
            icon: <Archive />,
            onClick: resource.isDisabled ? onActivate : onDisable,
          },
        ]
      : []),
    ...(type === ResourcePageType.Document || type === ResourcePageType.Download
      ? [
          {
            id: 'download',
            label: t('resources.list.columns.actions.download'),
            icon: <Download />,
            onClick: onDownload,
          },
        ]
      : []),
    ...(type === ResourcePageType.Video
      ? [
          {
            id: 'play',
            label: t('resources.list.columns.actions.play'),
            icon: <Play />,
            onClick: onPlay,
          },
        ]
      : []),
    ...(hasBookmarksWritePermission
      ? [
          {
            id: 'bookmark',
            label: personalBookmark
              ? t('resources.list.columns.actions.remove-bookmark')
              : t('resources.list.columns.actions.add-bookmark'),
            icon: <Bookmark />,
            onClick: () => {
              !personalBookmark
                ? onBookmarkAdd()
                : onBookmarkRemove(personalBookmark?.id as unknown as string);
            },
          },
        ]
      : []),
    ...(hasBookmarksWritePermission && getIsOtpProAndHigher(user)
      ? [
          {
            id: 'team-bookmark',
            label: teamBookmark
              ? t('resources.list.columns.actions.remove-team-bookmark')
              : t('resources.list.columns.actions.add-team-bookmark'),
            icon: <GroupBookmark />,
            onClick: () => {
              !teamBookmark
                ? onBookmarkAdd({
                    organizationId: user?.organizationId as string,
                  })
                : onBookmarkRemove(teamBookmark?.id as unknown as string);
            },
          },
        ]
      : []),
  ];

  const icons = [
    ...(personalBookmark
      ? [
          {
            icon: <BookmarkAction {...personalBookmark} />,
          },
        ]
      : []),
    ...(teamBookmark
      ? [
          {
            icon: <BookmarkAction {...teamBookmark} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="flex justify-end">
        <ActionsColumnCommon actions={actions} icons={icons} />
        <DeleteConfirmation
          objectTitle={resource.name}
          objectType={t('resources.list.resource-entity')}
          open={isDeleting}
          onClose={() => setIsDeleting(false)}
          onConfirm={onDelete}
        />
      </div>
      {type === ResourcePageType.Video && isPlaying && (
        <VideoModal
          resource={fullResource?.resourceById}
          onClose={onClosePlay}
        />
      )}
    </>
  );
};
